import { getRequest, postRequest } from "./apiService";

/**
 * Service for managing Interpretation Guide data
 */

const BASE_URL =
  "https://lexai-api-74624883720.us-central1.run.app/insureai/ml/intake/applications/v2";

/**
 * Get headers with auth tokens
 */
const getHeaders = () => ({
  accept: "application/json",
  "Content-Type": "application/json",
  "X-Access-Token": localStorage.getItem("access_token"),
  "X-Refresh-Token": localStorage.getItem("refresh_token")
});

/**
 * Convert kebab-case to title case for display
 * @param {string} kebabCase - The kebab-case string
 * @returns {string} - Title case string
 */
export const kebabToTitle = kebabCase => {
  return kebabCase
    .split("-")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Convert kebab-case to snake_case for API calls
 * @param {string} kebabCase - The kebab-case string
 * @returns {string} - Snake case string
 */
export const kebabToSnake = kebabCase => {
  return kebabCase.replace(/-/g, "_");
};

/**
 * Get Lucide icon for LOB based on name
 * @param {string} lobName - The LOB name
 * @returns {string} - Lucide icon name
 */
export const getLOBIcon = lobName => {
  const iconMap = {
    "general-liability": "Shield",
    "commercial-property": "Building",
    "commercial-auto": "Car",
    "workers-compensation": "HardHat",
    umbrella: "Umbrella",
    "business-owners-policy": "Briefcase",
    "cyber-liability": "Shield",
    "employment-practices": "Users",
    "professional-liability": "GraduationCap",
    "directors-officers": "Crown",
    marine: "Ship",
    aviation: "Plane"
  };

  return iconMap[lobName] || "FileText";
};

/**
 * Get all Lines of Business (LOBs)
 * @returns {Promise<Array>} - Array of LOBs in kebab-case
 */
export const getLOBs = async () => {
  try {
    const response = await getRequest(`${BASE_URL}/get_dif_lobs`, getHeaders());
    return response;
  } catch (error) {
    console.error("Error fetching LOBs:", error);
    throw error;
  }
};

/**
 * Get interpretation guide content for a specific LOB
 * @param {string} lob - The Line of Business identifier in snake_case
 * @returns {Promise<object>} - Object with uri and html content
 */
export const getInterpretationGuideContent = async lob => {
  try {
    const response = await getRequest(
      `${BASE_URL}/get_dif_interpretation?lob=${lob}`,
      getHeaders()
    );
    return response;
  } catch (error) {
    console.error("Error fetching interpretation guide content:", error);
    throw error;
  }
};

/**
 * Save interpretation guide content for a specific LOB
 * @param {string} lob - The Line of Business identifier in snake_case
 * @param {string} htmlContent - The HTML content to save
 * @returns {Promise<any>} - Response from the API
 */
export const saveInterpretationGuideContent = async (lob, htmlContent) => {
  try {
    const url = `${BASE_URL}/edit_dif_interpretation?lob=${encodeURIComponent(lob)}`;
    const headers = {
      ...getHeaders(),
      "Content-Type": "text/html"
    };
    const response = await postRequest(url, htmlContent, headers);
    return response;
  } catch (error) {
    console.error("Error saving interpretation guide content:", error);
    throw error;
  }
};
